import React from "react";
import { withStyles } from "@material-ui/core";
import DataGrid from '@dataGrid';
import { observer, inject } from "mobx-react";
import { autorun } from "mobx";
import { isEqual } from "lodash/lang";
import GridConfig from "./gridConfig";
import { withUserProfile } from "../../../../userProfile/userProfileContext";
import { getDataWithQueryString, postData } from "../../../../api/componentAPIs";
import { compose } from "recompose";
import { withSnackbar } from "notistack";
import StatusBar from '@dataGrid/components/StatusBar';
import GeocodingModal from "../../Common/Geocoding/GeoCodingModal";
import { getCustomerManagementGeocodingData } from "../../Common/Geocoding/utils";
import Events from "../../../../utilities/EventEmitter";
import { getFilteredData } from "../../../common/filterUtilities";
import { withAppConfig } from "../../../../appConfigContext";
import { getHolidayOptions } from "../EditCustomer/utils"
import MESSAGES from "../../../../appMessages";
import { getCustomerHolidayValidationsProps } from "../EditCustomer/components/Holidays/utils";
import styles from "./styles";
import appConstants from "../../../../appConstants";
import { getSpecialNeedsOptionsWithServiceLevel } from "../../utils";
import { getUniqueCustomerFilterData } from "../utils";

@inject("IBPlanningStore")
@observer
class Customers extends React.Component {
  constructor(props) {
    super(props);
    this.gridConfig = new GridConfig({
      userConfig: this.props.userData.userProfile?.columnPref,
      featureFlags: {
        enableFMECustomer: this.props.configJSON?.enableFMECustomer,
        enableNoCargoVanCustomer : this.props.configJSON?.enableNoCargoVanCustomer,
        enableFixedEquipmentSize: this.props.configJSON?.enableFixedEquipmentSize,
        enableTrailerBackFillCustomer: this.props.configJSON?.enableTrailerBackFillCustomer,
        enableRequiresAppointmentInCustomerManagement: this.props.configJSON?.enableRequiresAppointmentInCustomerManagement,
        enableIgnoreAppointmentInCustomerManagement: this.props.configJSON?.enableIgnoreAppointmentInCustomerManagement,
        enableCommentsForCustomer: this.props.configJSON?.enableCommentsForCustomer
      }
    });
    this.state = {
      quickFilterText: "",
      geoCodeCustomer: null,
      customerData: null,
      terminalId: this.props.userData.userProfile.terminal.id,
      quickFilter: null,
    };
    this.serviceLevelList = this.props.IBPlanningStore.serviceLevelList;
    this.gridContext = {
      specialNeedsOptions: getSpecialNeedsOptionsWithServiceLevel(this.serviceLevelList),
    };
    this.gridHeaderId = `gridHeader_${this.props.glContainer._config.id}`;
    this.selectedCustomers = [];
  }

  componentDidMount() {
    this._isMounted = true;
    this.props.glEventHub.on(this.gridHeaderId, this.onSearchTextChanged);
    this.props.glEventHub.on(
      `${this.gridHeaderId}_geocode`,
      this.onGeocodeCustomer
    );
    Events.on("updateCustomersData", this.updateCustomersData);
    this.props.glEventHub.on(
      "updateQuickFilter",
      this.updateCustomerQuickFilter
    );
    this.props.glEventHub.on(
      `activeTabSwitched_${appConstants.gridNames.CustomerGrid}`,
      this.onSelectionChanged
    );
    this.getCustomerData();
    this.autorunPriorityShipmentList = autorun(() => {
      if (
        !isEqual(
          this.serviceLevelList,
          this.props.IBPlanningStore.serviceLevelList
        )
      ) {
        this.serviceLevelList = this.props.IBPlanningStore.serviceLevelList;
        this.gridContext = {
          specialNeedsOptions: getSpecialNeedsOptionsWithServiceLevel(this.serviceLevelList),
        };
      }
    });
    this.fetchHolidays();
    this.props.glEventHub.on("RefreshCustomerData", this.getCustomerData);
    this.props.glEventHub.on(
      `${this.gridHeaderId}_BulkUpdate`,
      this.onBulkUpdate
    );
  }

  componentDidUpdate(prevProps) {
    if (this.props.userData.userProfile.terminal.id !== prevProps.userData.userProfile.terminal.id) {
      this.getCustomerData(false);
      this.fetchHolidays();
    }
   
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.props.glEventHub.off(this.gridHeaderId, this.onSearchTextChanged);
    this.props.glEventHub.off(
      `${this.gridHeaderId}_geocode`,
      this.onGeocodeCustomer
    );
    Events.off("updateCustomersData", this.updateCustomersData);
    this.props.glEventHub.off(
      "updateQuickFilter",
      this.updateCustomerQuickFilter
    );
    this.props.glEventHub.off(
      `activeTabSwitched_${appConstants.gridNames.CustomerGrid}`,
      this.onSelectionChanged
    );
    this.props.glEventHub.off("RefreshCustomerData", this.getCustomerData);
    this.props.glEventHub.off(
      `${this.gridHeaderId}_BulkUpdate`,
      this.onBulkUpdate
    );
    this.autorunPriorityShipmentList?.();
  }

  fetchHolidays = async () => {
    const holidayOptions = await getHolidayOptions({
      enqueueSnackbar: this.props.enqueueSnackbar,
      terminalId: this.props.userData.userProfile.terminal.id,
    });
    this.props.IBPlanningStore.setHolidayOptions(holidayOptions);
  };

  onBulkUpdate = ({ field, newValue }) => {
    let customerList = this.gridApi.getSelectedRows();
    if (field === 'holidays') {
      customerList = customerList
        .filter((customer) => {
          const { isHolidayAlreadyAdded, isSameDayAlreadyAdded } =
            getCustomerHolidayValidationsProps(customer[field], newValue);
          return !(isHolidayAlreadyAdded || isSameDayAlreadyAdded);
        })
        .map((customer) => ({
          customerCode: customer.deliveryDetails.originalCustomerCode,
          customerName: customer.name,
          [field]: customer[field]
        }));
    }

    if (
      this.isUpdateValid({
        customerList,
        field,
        value: newValue,
        isBulkEdit: true
      })
    ) {
      this.updateCustomersUI({ customerList, field, value: newValue });
      this.updateCustomersInfo({ customerList, field, value: newValue });
    }
  };

  updateCustomersUI = ({ customerList, field, value }) => {
    this.customersBackupData = {};
    const customerCodeList = customerList.map((customer) => customer.customerCode);

    this.gridApi.forEachNode((node) => {
      if (customerCodeList.includes(node.data.deliveryDetails.originalCustomerCode)) {
        this.customersBackupData[node.data.deliveryDetails.originalCustomerCode] = {
          [field]: [...node.data[field]]
        };
        if (field === 'holidays') {
          node.data[field].push({
            holidayId: value.value,
            holidayName: value.label,
            holidayDate: value.labelSuffix
          });
        } else {
          node.data[field] = value;
        }
      }
    });
    this.gridApi.refreshCells();
  };

  updateCustomersInfo = ({ customerList, field, value }) => {
    const requestParams = this.getBulkUpdateCustomerPayload({ customerList, value });
    this.props.glEventHub.emit(`${this.gridHeaderId}_DisableEditSave`, true);
    const { headerTooltip } = this.gridApi.getColumnDef(field);

    postData('BulkUpdateCustomer', requestParams).then(
      (response) => {
        if (response && Math.trunc(response.status / 100) === 2) {
          this.props.enqueueSnackbar(`${headerTooltip} updated`, {
            variant: 'success'
          });
          this.customersBackupData = {};
          this.props.glEventHub.emit(`${this.gridHeaderId}_showEditMode`, false);
          this.updateCustomersData(requestParams.customerCodes);
          this.props.glEventHub.emit(`${this.gridHeaderId}_DisableEditSave`, false);
          return;
        }
        this.handleUpdateCustomersInfoError({
          customerList,
          field,
          headerTooltip,
          statusCode: response.status
        });
      },
      () => {
        this.handleUpdateCustomersInfoError({ customerList, field, headerTooltip });
      }
    );
  };

  getBulkUpdateCustomerPayload = ({ customerList, value }) => {
    return {
      terminalId: this.props.userData.userProfile.terminal.id,
      holidayId: value?.value,
      customerCodes: customerList.map((customer) => customer.customerCode)
    };
  };

  handleUpdateCustomersInfoError = ({ customerList, field, headerTooltip, statusCode }) => {
    this.props.glEventHub.emit(`${this.gridHeaderId}_DisableEditSave`, false);
    this.restoreCustomerData({ customerList, field });
    if (statusCode === 403) {
      return;
    }
    this.props.enqueueSnackbar(`Failed to update ${headerTooltip}`, {
      variant: 'error'
    });
  };

  restoreCustomerData = ({ customerList, field }) => {
    const customerCodeList = customerList.map((customer) => customer.customerCode);
    this.gridApi.forEachNode((node) => {
      if (customerCodeList.includes(node.data.deliveryDetails.originalCustomerCode)) {
        node.data[field] = this.customersBackupData[node.data.deliveryDetails.originalCustomerCode][field];
      }
    });
    this.gridApi.refreshCells();
  };

  isUpdateValid = ({ customerList, field, value, isBulkEdit }) => {
    let isUpdateValid = true;
    if (!value) {
      this.props.enqueueSnackbar(MESSAGES.HOLIDAY.EMPTY, {
        variant: 'error'
      });
      isUpdateValid = false;
    } else if (field === 'holidays' && customerList.length === 0) {
      isBulkEdit &&
        this.props.enqueueSnackbar(MESSAGES.HOLIDAY.EXIST, {
          variant: 'error'
        });
      isUpdateValid = false;
    }
    return isUpdateValid;
  };

  updateCustomerQuickFilter = ( quickFilter) => {
    if (
      quickFilter.filterName ===
      appConstants.IBPLANNING_QUICKFILTER_NAMES.CUSTOMER_GRID
    ) {
      this.setState({
        quickFilter: quickFilter.eventData
      });
    }
  };

  static getDerivedStateFromProps(nextProps, state) {
    if (nextProps.userData.userProfile.terminal.id !== state.terminalId) {
      return {
        terminalId: nextProps.userData.userProfile.terminal.id,
        customerData: null,
      };
    }
    return null;
  }

  getCustomerData = (shouldUpdateIBStore=true) => {
    this.setState({
      customerData: null,
    });

    getDataWithQueryString(
      "GetCustomerDetail",
      `terminalId=${this.props.userData.userProfile.terminal.id}`
    ).then((response) => {
      if (!this._isMounted) {
        return;
      }
      const customerData = response.status === 200 ? response?.data ?? [] : [];
      this.setState({ customerData }, this.updateQuickFilterData);
    });
    shouldUpdateIBStore && Events.emit(appConstants.Event.RESTOREIBSTORE); // grouped customer equipment property update should be reflect in shipment/stops may affect other data, we should update the same way we are doing when these property get updated from edit customer popup.
  };

  updateCustomersData = (customerCodes, customerData=null) => {
    if (customerData) {
      this.updateCustomerInformation(customerData);
    } else {
      this.updateCustomersDataByCode(customerCodes);
    }
  };

  updateCustomerInformation = (customerData) => {
    if (!customerData.deliveryDetails) {
      Events.emit('updateShipper', [customerData]); // if update is only for shipper , then directly update in shippers grid without updating customers
      return;
    }

    const updatedCustomers = this.state.customerData.map((customer) => {
      return customer.deliveryDetails.originalCustomerCode ===
        customerData.deliveryDetails.originalCustomerCode
        ? customerData
        : customer;
    });

    customerData.pickupDetails && Events.emit('updateShipper', updatedCustomers); // Check for global update (will update shippers only if customer being updated is also a shipper)
    this.setState(
      {
        customerData: updatedCustomers
      },
      this.updateQuickFilterData
    );
  };

  updateCustomersDataByCode = (customerCodes) => {
    getDataWithQueryString(
      'GetCustomerDetail',
      `terminalId=${this.props.userData.userProfile.terminal.id}&customerCodes=${customerCodes.join(
        ','
      )}`
    ).then((response) => {
      const updatedCustomer = response.status === 200 ? response?.data ?? [] : [];
      const customerData = this.state.customerData;
        updatedCustomer.forEach((customer) => {
          if(customer.deliveryDetails){
            const index = customerData.findIndex(
              (c) =>
                c.deliveryDetails.originalCustomerCode ===
                customer.deliveryDetails.originalCustomerCode
            );
            customerData[index] = customer;
          }
        });
        
        Events.emit("updateShipper", updatedCustomer); // Check for global update
        this.setState({ customerData }, this.updateQuickFilterData);
    });
  };

  updateQuickFilterData = () => {
    const filterData = getUniqueCustomerFilterData(this.state.customerData);
    this.props.glEventHub.emit("updatedCustomerData", filterData);
  };

  onGeocodeCustomer = () => {
    const selectedCustomers = this.gridApi.getSelectedRows();
    if (!selectedCustomers.length) {
      return;
    }

    this.setState({
      geoCodeCustomer: {
        ...getCustomerManagementGeocodingData(selectedCustomers[0]),
        proId: "",
      },
    });
  };

  onSearchTextChanged = (searchText) => {
    this.setState({
      quickFilterText: searchText,
    });
  };

  onGridReady = ({ api }) => {
    this.gridApi = api;
    this.gridApi.runCommand = this.runCommand;
  };

  runCommand = (command, data) => {
    if (command === "openGeoCodeModel") {
       /* once Latitude/Longitude get updated using geocoding popup in grid, we need to show updated marker in geocoding popup map */
      const selectedCustomer = this.state.customerData.find(
        (customer) => data.code === customer.code,
      );
      this.setState({
        geoCodeCustomer: getCustomerManagementGeocodingData(selectedCustomer),
      });
    }
  };

  onSelectionChanged = () => {
    this.selectedCustomers = this.gridApi.getSelectedRows();
    this.props.glEventHub.emit(
      `customerSelectedonGrid`,
      this.selectedCustomers
    );
  };

  closeGeocodeModal = (hasDataChanged = false) => {
    if (hasDataChanged) {
      this.updateCustomersData([this.state.geoCodeCustomer.originalCustomerCode]);
    }
    this.setState({
      geoCodeCustomer: null,
    });
  };

  onCellClicked = (params) => {
    if(params.column.colId === "name")
      Events.emit('OpenEditCustomerDialog', params?.node?.data.deliveryDetails.originalCustomerCode);
  };

  constructStatusBar = (customerList) => {
    return [
      {
        label: `${customerList.length} Customers`,
      },
    ];
  };

  onRowDataUpdated = () => {
    const selectedCustomerCodes = this.selectedCustomers.map(
      (customer) => customer?.deliveryDetails?.originalCustomerCode
    );
    this.gridApi.forEachNode?.((node) => {
      if (selectedCustomerCodes.includes(node.data?.deliveryDetails?.originalCustomerCode)) {
        node.setSelected(true, false, true); // props are isSelected, clearOtherSelection, suppressFinishActions
      } else {
        // disabling supress finish actions calls onSelectionChanged event for each selection
        node.setSelected(false, false, true); // but we want single call of onSelectionChanged for multiselection , behaviour observed through user action
      } // so we suppress the call from setSelected and call it explicitly after loop completes
    });
    this.gridApi.dispatchEvent({
      type: "selectionChanged",
      api: this.gridApi,
    });
  };

  hasActiveColumnAndQuickFilter = () => {
    return (
      this.gridApi.isColumnFilterPresent() &&
      (this.props.IBPlanningStore.quickFilter || this.gridApi.isQuickFilterPresent())
    );
  };

  render() {
    const customerData = this.state.quickFilter
      ? getFilteredData(
          this.state.customerData ?? [],
          this.state.quickFilter,
          new Date()
        )
      : this.state.customerData?.slice();
    return (
      <div className={this.props.classes.dataGrid}>
        <DataGrid
          key={this.props.userData.userProfile.terminal.id}
          defaultColDef={this.gridConfig.config.defaultColDef}
          columnDefs={this.gridConfig.config.columns}
          config={this.gridConfig.config}
          rowSelection={this.gridConfig.config.rowSelection}
          rowDeselection={this.gridConfig.config.rowDeselection}
          rowData={customerData}
          deltaRowDataMode={true}
          getRowId={(params) => params.data.code}
          onRowDataUpdated={this.onRowDataUpdated}
          onGridReady={this.onGridReady}
          onSelectionChanged={this.onSelectionChanged}
          layoutContainer={this.props.glContainer}
          quickFilterText={this.state.quickFilterText}
          onCellClicked={this.onCellClicked}
          animateRows={false}
          panelLabel={(filteredData) => (
            <StatusBar
              data={this.constructStatusBar(filteredData)}
              filterGridData={this.updateGridData}
            />
          )}
          hasActiveColumnAndQuickFilter={this.hasActiveColumnAndQuickFilter}
          context={this.gridContext}
        />
        {this.state.geoCodeCustomer && (
          <GeocodingModal
            customer={this.state.geoCodeCustomer}
            userData={this.props.userData.userProfile}
            close={this.closeGeocodeModal}
          />
        )}
      </div>
    );
  }
}

export default compose(withStyles(styles), withUserProfile, withSnackbar, withAppConfig)(Customers);
